import React, { useState, useMemo, useCallback, useEffect } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import { SetupFilterBar } from '../components';
import { ZipcodeRecord, ZoneBadge, getZoneStyle } from '../data/location-shared';

// ── Types ──

interface AreasTabProps {
  brand: BrandConfig;
  Icons: IconsType;
  zipcodes: ZipcodeRecord[];
}

type ViewMode = 'county' | 'zone';

interface ZipEntry {
  city: string;
  zipCode: number;
  alias: string;
}

// ── Zone filter options ──

const zoneFilterOptions = [
  { value: 'Remote Area', label: 'Remote' },
  { value: 'Suburban Area', label: 'Suburban' },
  { value: 'Urban Area', label: 'Urban' },
];

// ── Helpers ──

function zoneShortLabel(zone: string): string {
  return zone.replace(' Area', '').replace(' Court', '');
}

// ── Component ──

const AreasTab: React.FC<AreasTabProps> = ({ brand, Icons, zipcodes }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('county');
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  // ── Build county view tree ──

  const countyTree = useMemo(() => {
    const map = new Map<string, Map<string, ZipEntry[]>>();

    for (const rec of zipcodes) {
      const county = rec.County;
      const zone = rec['Pricing Zone'];
      if (!map.has(county)) map.set(county, new Map());
      const zoneMap = map.get(county);
      if (!zoneMap.has(zone)) zoneMap.set(zone, []);
      zoneMap.get(zone).push({
        city: rec.City,
        zipCode: rec['Zip Code'],
        alias: rec['City Alias Name'],
      });
    }

    // Deduplicate entries within each zone (same city + zip = one row)
    const result: {
      county: string;
      zones: { zone: string; entries: ZipEntry[] }[];
      totalZips: number;
      zoneCounts: Record<string, number>;
    }[] = [];

    for (const [county, zoneMap] of map) {
      const zones: { zone: string; entries: ZipEntry[] }[] = [];
      const zoneCounts: Record<string, number> = {};
      let totalZips = 0;

      for (const [zone, entries] of zoneMap) {
        // Deduplicate by city+zip
        const seen = new Set<string>();
        const deduped: ZipEntry[] = [];
        for (const e of entries) {
          const key = `${e.city}|${e.zipCode}`;
          if (!seen.has(key)) {
            seen.add(key);
            deduped.push(e);
          }
        }
        zones.push({ zone, entries: deduped.sort((a, b) => a.city.localeCompare(b.city) || a.zipCode - b.zipCode) });
        zoneCounts[zone] = deduped.length;
        totalZips += deduped.length;
      }

      zones.sort((a, b) => a.zone.localeCompare(b.zone));
      result.push({ county, zones, totalZips, zoneCounts });
    }

    result.sort((a, b) => a.county.localeCompare(b.county));
    return result;
  }, [zipcodes]);

  // ── Build zone view tree ──

  const zoneTree = useMemo(() => {
    const map = new Map<string, Map<string, ZipEntry[]>>();

    for (const rec of zipcodes) {
      const zone = rec['Pricing Zone'];
      const county = rec.County;
      if (!map.has(zone)) map.set(zone, new Map());
      const countyMap = map.get(zone);
      if (!countyMap.has(county)) countyMap.set(county, []);
      countyMap.get(county).push({
        city: rec.City,
        zipCode: rec['Zip Code'],
        alias: rec['City Alias Name'],
      });
    }

    const result: {
      zone: string;
      counties: { county: string; entries: ZipEntry[] }[];
      totalCounties: number;
      totalZips: number;
    }[] = [];

    for (const [zone, countyMap] of map) {
      const counties: { county: string; entries: ZipEntry[] }[] = [];
      let totalZips = 0;

      for (const [county, entries] of countyMap) {
        const seen = new Set<string>();
        const deduped: ZipEntry[] = [];
        for (const e of entries) {
          const key = `${e.city}|${e.zipCode}`;
          if (!seen.has(key)) {
            seen.add(key);
            deduped.push(e);
          }
        }
        counties.push({ county, entries: deduped.sort((a, b) => a.city.localeCompare(b.city) || a.zipCode - b.zipCode) });
        totalZips += deduped.length;
      }

      counties.sort((a, b) => a.county.localeCompare(b.county));
      result.push({ zone, counties, totalCounties: counties.length, totalZips });
    }

    // Sort: Remote, Suburban, Urban
    const order = ['Remote Area', 'Suburban Area', 'Urban Area'];
    result.sort((a, b) => order.indexOf(a.zone) - order.indexOf(b.zone));
    return result;
  }, [zipcodes]);

  // ── Filtering ──

  const matchesSearch = useCallback((entry: ZipEntry): boolean => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      entry.city.toLowerCase().includes(q) ||
      String(entry.zipCode).includes(q) ||
      entry.alias.toLowerCase().includes(q)
    );
  }, [search]);

  const matchesCountySearch = useCallback((county: string): boolean => {
    if (!search) return true;
    return county.toLowerCase().includes(search.toLowerCase());
  }, [search]);

  // Auto-expand matching branches when searching
  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- auto-expand matching branches on search change */
  useEffect(() => {
    if (!search) return;
    const q = search.toLowerCase();
    const keysToExpand = new Set<string>();

    if (viewMode === 'county') {
      for (const c of countyTree) {
        if (zoneFilter !== 'all') {
          const hasZone = c.zones.some(z => z.zone === zoneFilter);
          if (!hasZone) continue;
        }
        const countyMatches = c.county.toLowerCase().includes(q);
        for (const z of c.zones) {
          if (zoneFilter !== 'all' && z.zone !== zoneFilter) continue;
          const hasMatchingEntry = z.entries.some(e =>
            e.city.toLowerCase().includes(q) ||
            String(e.zipCode).includes(q) ||
            e.alias.toLowerCase().includes(q),
          );
          if (countyMatches || hasMatchingEntry) {
            keysToExpand.add(`county:${c.county}`);
            if (hasMatchingEntry) {
              keysToExpand.add(`county:${c.county}:${z.zone}`);
            }
          }
        }
      }
    } else {
      for (const z of zoneTree) {
        if (zoneFilter !== 'all' && z.zone !== zoneFilter) continue;
        for (const c of z.counties) {
          const countyMatches = c.county.toLowerCase().includes(q);
          const hasMatchingEntry = c.entries.some(e =>
            e.city.toLowerCase().includes(q) ||
            String(e.zipCode).includes(q) ||
            e.alias.toLowerCase().includes(q),
          );
          if (countyMatches || hasMatchingEntry) {
            keysToExpand.add(`zone:${z.zone}`);
            if (hasMatchingEntry) {
              keysToExpand.add(`zone:${z.zone}:${c.county}`);
            }
          }
        }
      }
    }

    if (keysToExpand.size > 0) {
      setExpanded(keysToExpand);
    }
  }, [search, viewMode, zoneFilter, countyTree, zoneTree]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  // ── Expand/Collapse ──

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    const keys = new Set<string>();
    if (viewMode === 'county') {
      for (const c of countyTree) {
        keys.add(`county:${c.county}`);
        for (const z of c.zones) {
          keys.add(`county:${c.county}:${z.zone}`);
        }
      }
    } else {
      for (const z of zoneTree) {
        keys.add(`zone:${z.zone}`);
        for (const c of z.counties) {
          keys.add(`zone:${z.zone}:${c.county}`);
        }
      }
    }
    setExpanded(keys);
  };

  const collapseAll = () => setExpanded(new Set());

  // ── Stats ──

  const stats = useMemo(() => {
    const uniqueCounties = new Set(zipcodes.map(r => r.County)).size;
    const uniqueZips = new Set(zipcodes.map(r => r['Zip Code'])).size;
    const remote = new Set(zipcodes.filter(r => r['Pricing Zone'] === 'Remote Area').map(r => r['Zip Code'])).size;
    const suburban = new Set(zipcodes.filter(r => r['Pricing Zone'] === 'Suburban Area').map(r => r['Zip Code'])).size;
    const urban = new Set(zipcodes.filter(r => r['Pricing Zone'] === 'Urban Area').map(r => r['Zip Code'])).size;
    const areas = new Set(zipcodes.map(r => r.Name)).size;
    return { areas, counties: uniqueCounties, zips: uniqueZips, remote, suburban, urban };
  }, [zipcodes]);

  // ── Filtered trees ──

  const filteredCountyTree = useMemo(() => {
    return countyTree
      .map(c => {
        const zones = c.zones
          .filter(z => zoneFilter === 'all' || z.zone === zoneFilter)
          .map(z => ({
            ...z,
            entries: search ? z.entries.filter(matchesSearch) : z.entries,
          }))
          .filter(z => z.entries.length > 0);

        if (zones.length === 0 && !matchesCountySearch(c.county)) return null;
        if (zones.length === 0 && matchesCountySearch(c.county)) {
          // County name matches but no entries -- show all zones
          const allZones = c.zones
            .filter(z => zoneFilter === 'all' || z.zone === zoneFilter);
          if (allZones.length === 0) return null;
          return { ...c, zones: allZones, totalZips: allZones.reduce((s, z) => s + z.entries.length, 0) };
        }

        return { ...c, zones, totalZips: zones.reduce((s, z) => s + z.entries.length, 0) };
      })
      .filter(Boolean) as typeof countyTree;
  }, [countyTree, zoneFilter, search, matchesSearch, matchesCountySearch]);

  const filteredZoneTree = useMemo(() => {
    return zoneTree
      .filter(z => zoneFilter === 'all' || z.zone === zoneFilter)
      .map(z => {
        const counties = z.counties
          .map(c => ({
            ...c,
            entries: search ? c.entries.filter(matchesSearch) : c.entries,
          }))
          .filter(c => c.entries.length > 0 || matchesCountySearch(c.county));

        return {
          ...z,
          counties,
          totalCounties: counties.length,
          totalZips: counties.reduce((s, c) => s + c.entries.length, 0),
        };
      })
      .filter(z => z.counties.length > 0);
  }, [zoneTree, zoneFilter, search, matchesSearch, matchesCountySearch]);

  // ── Render ──

  const ChevronIcon = Icons.ChevronRight;

  return (
    <div>
      {/* Stats Bar */}
      <div
        className="flex flex-wrap gap-4 mb-4 px-4 py-2 rounded-lg text-sm"
        style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
      >
        <span><strong>{stats.areas}</strong> areas</span>
        <span>|</span>
        <span><strong>{stats.counties}</strong> counties</span>
        <span>|</span>
        <span><strong>{stats.zips}</strong> zips</span>
        <span>|</span>
        <span style={{ color: '#374151' }}><strong>{stats.remote}</strong> remote</span>
        <span>|</span>
        <span style={{ color: '#1E40AF' }}><strong>{stats.suburban}</strong> suburban</span>
        <span>|</span>
        <span style={{ color: '#065F46' }}><strong>{stats.urban}</strong> urban</span>
      </div>

      {/* View Toggle + Filter Bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: brand.colors.border }}>
          <button
            onClick={() => { setViewMode('county'); setExpanded(new Set()); }}
            className="px-3 py-1.5 text-sm font-medium transition-colors"
            style={{
              backgroundColor: viewMode === 'county' ? brand.colors.primary : 'white',
              color: viewMode === 'county' ? brand.colors.onPrimary : brand.colors.text,
            }}
          >
            By County
          </button>
          <button
            onClick={() => { setViewMode('zone'); setExpanded(new Set()); }}
            className="px-3 py-1.5 text-sm font-medium transition-colors"
            style={{
              backgroundColor: viewMode === 'zone' ? brand.colors.primary : 'white',
              color: viewMode === 'zone' ? brand.colors.onPrimary : brand.colors.text,
            }}
          >
            By Zone
          </button>
        </div>

        <div className="flex-1">
          <SetupFilterBar
            brand={brand}
            Icons={Icons}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search city, zip, or county..."
            statusFilter={zoneFilter}
            statusOptions={zoneFilterOptions}
            onStatusChange={setZoneFilter}
          />
        </div>
      </div>

      {/* Expand/Collapse controls */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={expandAll}
          className="px-2 py-1 text-xs rounded border"
          style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
        >
          Expand All
        </button>
        <button
          onClick={collapseAll}
          className="px-2 py-1 text-xs rounded border"
          style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
        >
          Collapse All
        </button>
      </div>

      {/* Tree View */}
      <div className="border rounded-lg overflow-hidden" style={{ borderColor: brand.colors.border }}>
        {viewMode === 'county' ? (
          /* ── County View ── */
          filteredCountyTree.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm" style={{ color: brand.colors.textLight }}>
              No areas match your filters
            </div>
          ) : (
            filteredCountyTree.map(c => {
              const countyKey = `county:${c.county}`;
              const isCountyExpanded = expanded.has(countyKey);

              return (
                <div key={c.county}>
                  {/* County row */}
                  <button
                    onClick={() => toggleExpand(countyKey)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 border-b"
                    style={{ borderColor: brand.colors.border }}
                  >
                    <ChevronIcon
                      className="w-3.5 h-3.5 transition-transform flex-shrink-0"
                      style={{
                        color: brand.colors.textLight,
                        transform: isCountyExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      }}
                    />
                    <span className="font-semibold" style={{ color: brand.colors.text }}>
                      {c.county}
                    </span>
                    <div className="flex gap-1.5 ml-2">
                      {c.zones.map(z => {
                        const style = getZoneStyle(z.zone);
                        return (
                          <span
                            key={z.zone}
                            className="px-1.5 py-0 rounded text-[10px] font-medium"
                            style={{ backgroundColor: style.bg, color: style.text }}
                          >
                            {z.entries.length} {zoneShortLabel(z.zone)}
                          </span>
                        );
                      })}
                    </div>
                    <span className="ml-auto text-xs" style={{ color: brand.colors.textMuted }}>
                      {c.totalZips} zips
                    </span>
                  </button>

                  {/* Expanded county content */}
                  {isCountyExpanded && c.zones.map(z => {
                    const zoneKey = `county:${c.county}:${z.zone}`;
                    const isZoneExpanded = expanded.has(zoneKey);
                    const uniqueCities = new Set(z.entries.map(e => e.city)).size;

                    return (
                      <div key={z.zone}>
                        {/* Zone sub-row */}
                        <button
                          onClick={() => toggleExpand(zoneKey)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 border-b"
                          style={{ borderColor: brand.colors.border, paddingLeft: '2.5rem' }}
                        >
                          <ChevronIcon
                            className="w-3 h-3 transition-transform flex-shrink-0"
                            style={{
                              color: brand.colors.textLight,
                              transform: isZoneExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            }}
                          />
                          <ZoneBadge zone={z.zone} />
                          <span className="text-xs" style={{ color: brand.colors.textMuted }}>
                            {uniqueCities} {uniqueCities === 1 ? 'city' : 'cities'}, {z.entries.length} zips
                          </span>
                        </button>

                        {/* Zip entries */}
                        {isZoneExpanded && (
                          <div>
                            {/* Table header */}
                            <div
                              className="flex px-4 py-1.5 text-xs font-medium border-b"
                              style={{
                                paddingLeft: '4.5rem',
                                borderColor: brand.colors.border,
                                backgroundColor: brand.colors.bgLight,
                                color: brand.colors.textLight,
                              }}
                            >
                              <span className="w-48">City</span>
                              <span className="w-24">Zip Code</span>
                              <span className="w-48">Alias</span>
                            </div>
                            {z.entries.map((e) => (
                              <div
                                key={`${e.city}-${e.zipCode}`}
                                className="flex px-4 py-1.5 text-sm border-b hover:bg-gray-50"
                                style={{ paddingLeft: '4.5rem', borderColor: brand.colors.border }}
                              >
                                <span className="w-48" style={{ color: brand.colors.text }}>{e.city}</span>
                                <span className="w-24 font-mono" style={{ color: brand.colors.text }}>
                                  {String(e.zipCode).padStart(5, '0')}
                                </span>
                                <span className="w-48" style={{ color: brand.colors.textMuted }}>
                                  {e.alias !== e.city ? e.alias : '-'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )
        ) : (
          /* ── Zone View ── */
          filteredZoneTree.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm" style={{ color: brand.colors.textLight }}>
              No areas match your filters
            </div>
          ) : (
            filteredZoneTree.map(z => {
              const zoneKey = `zone:${z.zone}`;
              const isZoneExpanded = expanded.has(zoneKey);

              return (
                <div key={z.zone}>
                  {/* Zone row */}
                  <button
                    onClick={() => toggleExpand(zoneKey)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 border-b"
                    style={{ borderColor: brand.colors.border }}
                  >
                    <ChevronIcon
                      className="w-3.5 h-3.5 transition-transform flex-shrink-0"
                      style={{
                        color: brand.colors.textLight,
                        transform: isZoneExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      }}
                    />
                    <ZoneBadge zone={z.zone} />
                    <span className="text-xs ml-1" style={{ color: brand.colors.textMuted }}>
                      {z.totalCounties} counties, {z.totalZips} zips
                    </span>
                  </button>

                  {/* Expanded zone content */}
                  {isZoneExpanded && z.counties.map(c => {
                    const countyKey = `zone:${z.zone}:${c.county}`;
                    const isCountyExpanded = expanded.has(countyKey);
                    const uniqueCities = new Set(c.entries.map(e => e.city)).size;

                    return (
                      <div key={c.county}>
                        {/* County sub-row */}
                        <button
                          onClick={() => toggleExpand(countyKey)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 border-b"
                          style={{ borderColor: brand.colors.border, paddingLeft: '2.5rem' }}
                        >
                          <ChevronIcon
                            className="w-3 h-3 transition-transform flex-shrink-0"
                            style={{
                              color: brand.colors.textLight,
                              transform: isCountyExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            }}
                          />
                          <span className="font-medium" style={{ color: brand.colors.text }}>
                            {c.county}
                          </span>
                          <span className="text-xs" style={{ color: brand.colors.textMuted }}>
                            ({uniqueCities} {uniqueCities === 1 ? 'city' : 'cities'}, {c.entries.length} zips)
                          </span>
                        </button>

                        {/* Zip entries */}
                        {isCountyExpanded && (
                          <div>
                            {/* Table header */}
                            <div
                              className="flex px-4 py-1.5 text-xs font-medium border-b"
                              style={{
                                paddingLeft: '4.5rem',
                                borderColor: brand.colors.border,
                                backgroundColor: brand.colors.bgLight,
                                color: brand.colors.textLight,
                              }}
                            >
                              <span className="w-48">City</span>
                              <span className="w-24">Zip Code</span>
                              <span className="w-48">Alias</span>
                            </div>
                            {c.entries.map((e) => (
                              <div
                                key={`${e.city}-${e.zipCode}`}
                                className="flex px-4 py-1.5 text-sm border-b hover:bg-gray-50"
                                style={{ paddingLeft: '4.5rem', borderColor: brand.colors.border }}
                              >
                                <span className="w-48" style={{ color: brand.colors.text }}>{e.city}</span>
                                <span className="w-24 font-mono" style={{ color: brand.colors.text }}>
                                  {String(e.zipCode).padStart(5, '0')}
                                </span>
                                <span className="w-48" style={{ color: brand.colors.textMuted }}>
                                  {e.alias !== e.city ? e.alias : '-'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )
        )}
      </div>
    </div>
  );
};

export default AreasTab;
